import { store } from "@store/index";
import {
  type SideNavigationV2Props,
  type PageV2Component,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "@schemas/sideNavigation.schema";
import {
  updateField,
  createField,
  updateFieldProp,
  updateFieldAppearance,
  updateFieldAiMeta,
  updateFieldSectionLocal,
  renameFieldSectionInStore,
  enableRegex,
  disableRegex,
  updateFieldHandle,
  updateFieldDefinitionProp,
  resetFieldDefinition,
  updateDefaultValueWithValidation,
  updateFieldPropWithAutoHandleAndValidate,
} from "@store/features/crm/crmSlice";
import { open as openDialog } from "@store/features/layout/dialogSlice";
import { DataType, FieldRequirement } from "skedyul-db/browser";
import { iconTypes, SpanCol } from "skedyul-ui/ui";
import { ChangeEvent } from "react";
import { getColorForOption } from "../../../utils/colors";
import { trpc } from "@utils/api";
import { parseErrorCodeToMessage } from "@schemas/crm/fields/errors";
import { toast } from "sonner";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import {
  openDeleteFieldDialog,
  openUpdateDefaultValueDialog,
} from "../../../lib/openMigrationDialog";

type UpdateFieldMutationResult = {
  success?: boolean;
  errors?: Array<{ message?: string }>;
};

const toValidationMessageRecord = (
  messages:
    | Record<string, string | null | undefined>
    | null
    | undefined,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(messages ?? {}).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );

const defaultValuesEqual = (
  a: { value?: unknown[]; optionIds?: string[] } | null | undefined,
  b: { value?: unknown[]; optionIds?: string[] } | null | undefined,
): boolean => {
  return (
    JSON.stringify(a?.value ?? []) === JSON.stringify(b?.value ?? []) &&
    JSON.stringify(a?.optionIds ?? []) === JSON.stringify(b?.optionIds ?? [])
  );
};

const handleUpdateFieldResponse = (response: {
  meta: { requestStatus: string };
  payload: unknown;
}): void => {
  if (response.meta.requestStatus !== "fulfilled") {
    toast.error("Failed to update field");
    return;
  }

  const payload = response.payload as UpdateFieldMutationResult;
  if (payload.success === false) {
    toast.error(payload.errors?.[0]?.message ?? "Failed to update field");
    return;
  }

  toast.success("Field updated");
};

export const editFieldViewSchema = (
  args: SideNavigationComponentProps<PageV2Component, { subdomain: string }>,
): SideNavigationV2Props<PageV2Component> => {
  const { subdomain } = args.routerContext.params;
  const router = args.routerContext.router;

  const {
    crm: {
      field: {
        data: field,
        options: fieldOptions,
        error: fieldError,
        isValidating,
        isLoading,
      },
      model: { data: model },
      models: { data: models },
      normalizations: { data: normalizations },
    },
    workplace: {
      workplace: { data: workplace },
    },
    sideNavigation: {
      right: { isOpen },
    },
  } = args.state;

  // Determine if we're in create mode (no field ID) or edit mode
  const isCreateMode = !field?.id;
  const isEditMode = !isCreateMode;

  // Communication channel linking was deprecated with appFields removal
  const hasCommunicationChannel = false;

  const workplaceSectionOptions = (() => {
    const byHandle = new Map<string, { handle: string; name: string }>();
    for (const m of models || []) {
      for (const f of m.fields || []) {
        const section = (
          f as { section?: { handle: string; name: string } | null }
        ).section;
        if (section?.handle && !byHandle.has(section.handle)) {
          byHandle.set(section.handle, {
            handle: section.handle,
            name: section.name,
          });
        }
      }
    }
    if (field?.section?.handle && !byHandle.has(field.section.handle)) {
      byHandle.set(field.section.handle, {
        handle: field.section.handle,
        name: field.section.name,
      });
    }
    return Array.from(byHandle.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  })();

  const dataTypeOptions = [
    { label: "String", value: DataType.STRING, icon: "Text" as iconTypes },
    {
      label: "Long Text",
      value: DataType.LONG_STRING,
      icon: "FileText" as iconTypes,
    },
    { label: "Number", value: DataType.NUMBER, icon: "Hash" as iconTypes },
    {
      label: "Boolean",
      value: DataType.BOOLEAN,
      icon: "ToggleLeft" as iconTypes,
    },
    { label: "Date", value: DataType.DATE, icon: "Calendar" as iconTypes },
    {
      label: "Date Time",
      value: DataType.DATE_TIME,
      icon: "CalendarClock" as iconTypes,
    },
    { label: "Time", value: DataType.TIME, icon: "Clock" as iconTypes },
  ];

  const hasErrors = (): boolean => {
    return (
      !!fieldError?.handle || !!fieldError?.label || !!fieldError?.defaultValue
    );
  };

  const hasRequiredFieldsToCreate = () => {
    return (
      field?.label &&
      model?.id &&
      field?.handle &&
      field?.type &&
      field?.definition?.handle
    );
  };

  const isUniqueHidden = () => {
    return field?.type === "BOOLEAN" || field?.type === DataType.LONG_STRING;
  };

  const isRegexHidden = () => {
    return field?.type !== DataType.STRING || !field?.definition?.isPrivate;
  };

  const isRegexPatternHidden = () => {
    return (
      !fieldOptions.regexEnabled ||
      field?.type !== DataType.STRING ||
      !field?.definition?.isPrivate
    );
  };

  const isOptionsHidden = () => {
    return (
      field?.type === DataType.BOOLEAN ||
      field?.type === DataType.LONG_STRING ||
      !field?.definition?.isPrivate
    );
  };

  const isNormalizationsHidden = () => {
    return (
      !field?.definition?.isPrivate ||
      field?.type === DataType.LONG_STRING ||
      field?.definition?.normalizations?.length === 0
    );
  };

  return {
    side: "right",
    isOpen,
    width: "700px",
    component: {
      component: "PageV2",
      props: {
        title: isCreateMode ? "New Field" : field?.label || "No Field Selected",
        hideTitle: true,
        header: {
          icon: "Tag",
          primaryAction: {
            label: isCreateMode ? "Create Field" : "Save Field",
            variant: "default",
            size: "sm",
            isLoading,
            isDisabled:
              hasErrors() ||
              (isCreateMode ? !hasRequiredFieldsToCreate() : !field?.id),
            handleClick: async () => {
              if (!field) {
                return;
              }

              try {
                // Auto-generate AI description if empty and mutability is not immutable
                let aiMeta = field.aiMeta;
                const mutability = aiMeta?.mutability || "always";
                if (
                  !aiMeta?.description &&
                  mutability !== "immutable" &&
                  !field.system
                ) {
                  try {
                    const result =
                      await trpc.crm.generateFieldAiDescription.mutate({
                        fieldLabel: field.label || "",
                        fieldType: field.type,
                        modelName: model?.name || "",
                        description: field.description,
                      });
                    aiMeta = {
                      ...aiMeta,
                      description: result.description,
                    };
                    // Update the local state with the generated description
                    store.dispatch(
                      updateFieldAiMeta({
                        key: "description",
                        value: result.description,
                      }),
                    );
                  } catch (aiError) {
                    console.warn(
                      "Failed to auto-generate AI description:",
                      aiError,
                    );
                    // Continue with save even if AI generation fails
                  }
                }

                if (isCreateMode) {
                  if (!field) {
                    return;
                  }

                  const definitionHandle = !field?.definition?.isPrivate
                    ? field?.definition?.handle || ""
                    : `${args.routerContext.params.subdomain || ""}/${model?.handle || ""}/${field.handle || ""}`;

                  // Sanitize defaultValue.optionIds to ensure only strings are sent
                  const sanitizedDefaultValue = {
                    value: field.defaultValue?.value || [],
                    optionIds: (field.defaultValue?.optionIds || []).filter(
                      (id): id is string => typeof id === "string",
                    ),
                  };

                  // Create new field
                  const response = await store.dispatch(
                    createField({
                      modelId: model?.id || "",
                      label: field.label || "",
                      description: field.description || "",
                      appearance: field.appearance || null,
                      handle: field.handle,
                      requirement: field.requirement,
                      isUnique: field.isUnique || false,
                      defaultValue: sanitizedDefaultValue,
                      type: field.type,
                      /**
                       * creates a definition for this field
                       */
                      definition: {
                        handle: definitionHandle,
                        type: field.type,
                        options: field?.definition?.options || [],
                        limitChoices: field?.definition?.limitChoices ?? null,
                        regexPattern: field?.definition?.regexPattern,
                        normalizations: field?.definition?.normalizations || [],
                      },
                      validationMessages: {
                        REGEX:
                          field?.validationMessages?.REGEX ||
                          "The value does not match the required format",
                        REQUIRED:
                          field?.validationMessages?.REQUIRED ||
                          "This field is required",
                        UNIQUE:
                          field?.validationMessages?.UNIQUE ||
                          "This field must be unique",
                      },
                      aiMeta: aiMeta || undefined,
                      sectionHandle: field.section?.handle ?? null,
                    }),
                  );

                  if (response.meta.requestStatus === "fulfilled") {
                    toast.success("Field created");
                    // Navigate to the new field URL
                    const createdField = response.payload as { id: string };
                    if (createdField?.id) {
                      const currentUrl = new URL(window.location.href);
                      currentUrl.searchParams.set("field", createdField.id);
                      args.routerContext.router.replace(
                        currentUrl.pathname + currentUrl.search,
                      );
                    }
                  } else {
                    toast.error("Failed to create field");
                  }
                } else {
                  // Check if default value has changed before showing dialog
                  if (
                    !defaultValuesEqual(
                      fieldOptions.originalDefaultValue,
                      field?.defaultValue,
                    )
                  ) {
                    // Use the unified migration dialog for default value changes
                    void openUpdateDefaultValueDialog(
                      field.id,
                      field.label || "Untitled Field",
                      {
                        value: fieldOptions.originalDefaultValue.value || [],
                        optionIds:
                          fieldOptions.originalDefaultValue.optionIds || [],
                      },
                      {
                        value: (field?.defaultValue?.value || []) as unknown[],
                        optionIds: field?.defaultValue?.optionIds || [],
                      },
                      {
                        id: field.id,
                        label: field.label,
                        description: field.description,
                        appearance: field.appearance,
                        requirement: field.requirement,
                        isUnique: field.isUnique,
                        defaultValue: {
                          value: field.defaultValue?.value || [],
                          optionIds: field.defaultValue?.optionIds || [],
                        },
                        definition:
                          field.definition && field.definition.id
                            ? {
                                id: field.definition.id,
                                workplaceId: workplace?.id || "",
                                handle: field.definition.handle || "",
                                limitChoices:
                                  field.definition.limitChoices ?? null,
                                regexPattern: field.definition.regexPattern,
                                normalizations:
                                  field.definition.normalizations || [],
                              }
                            : undefined,
                        validationMessages: {
                          REGEX:
                            field?.validationMessages?.REGEX ||
                            "The value does not match the required format",
                          REQUIRED:
                            field?.validationMessages?.REQUIRED ||
                            "This field is required",
                          UNIQUE:
                            field?.validationMessages?.UNIQUE ||
                            "This field must be unique",
                        },
                        aiMeta: aiMeta || undefined,
                        sectionHandle: field.section?.handle ?? null,
                      },
                      {
                        enabledStrategies: [
                          "replace-empty",
                          "replace-legacy",
                          "replace-all",
                        ],
                        allowMultiple:
                          field?.definition?.limitChoices === undefined ||
                          field?.definition?.limitChoices === null ||
                          field?.definition?.limitChoices > 1,
                      },
                    );
                  } else {
                    // Update existing field
                    const response = await store.dispatch(
                      updateField({
                        id: field.id,
                        label: field.label,
                        description: field.description,
                        appearance: field.appearance,
                        requirement: field.requirement,
                        isUnique: field.isUnique,
                        defaultValue: field.defaultValue,
                        definition:
                          field.definition && field.definition.id
                            ? {
                                id: field.definition.id,
                                workplaceId: workplace?.id || "",
                                handle: field.definition.handle || "",
                                limitChoices:
                                  field.definition.limitChoices ?? null,
                                regexPattern: field.definition.regexPattern,
                                normalizations:
                                  field.definition.normalizations || [],
                              }
                            : undefined,
                        validationMessages: {
                          REGEX:
                            field?.validationMessages?.REGEX ||
                            "The value does not match the required format",
                          REQUIRED:
                            field?.validationMessages?.REQUIRED ||
                            "This field is required",
                          UNIQUE:
                            field?.validationMessages?.UNIQUE ||
                            "This field must be unique",
                        },
                        aiMeta: aiMeta || undefined,
                        sectionHandle: field.section?.handle ?? null,
                      }),
                    );

                    handleUpdateFieldResponse(response);
                  }
                }
              } catch (error) {
                console.error(
                  `Failed to ${isCreateMode ? "create" : "update"} field:`,
                  error,
                );
              }
            },
          },
        },
        content: {
          structure: "sidebar",
          components: [
            {
              type: "Card",
              title: "Field Details",
              props: {
                header: {
                  title: "Field Details",
                  description: isCreateMode
                    ? "Create a new field for this model"
                    : field?.id
                      ? "Edit field configuration and properties"
                      : "No field is currently selected",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  id: "field-details-form",
                  fields: [
                    {
                      component: "Input",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text",
                        id: "field-name",
                        label: "Name",
                        leftIcon: "Text",
                        value: field?.label,
                        placeholder: "Enter your unique field name",
                        helpText:
                          "This is the human readable name, is should be singular and in title case, e.g. 'First Name' or 'Email'",
                        handleChange: (ev) => {
                          void store.dispatch(
                            updateFieldPropWithAutoHandleAndValidate({
                              key: "label",
                              value: ev.target.value,
                              subdomain: args.routerContext.params.subdomain,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Input",
                      span: 12,
                      mdSpan: 12,
                      lgSpan: 12,
                      props: {
                        type: "text",
                        id: "field-handle",
                        label: "Handle",
                        leftIcon: "Link",
                        value: field?.handle,
                        isDisabled: field?.id ? true : false,
                        placeholder: "Enter your unique field handle",
                        helpText:
                          "This is the unique identifier for the field, it should be lowercase and in kebab-case, e.g. 'first-name' or 'email'",
                        handleChange: (ev) => {
                          void store.dispatch(
                            updateFieldHandle({
                              handle: ev.target.value,
                              modelId: model?.id || "",
                              subdomain: args.routerContext.params.subdomain,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Textarea" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "fieldDescription",
                        label: "Description",
                        value: field?.description || "",
                        placeholder: "Enter a description for this field",
                        helpText:
                          "Explains what this field is for (not shown in forms)",
                        rows: 3,
                        handleChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
                          store.dispatch(
                            updateFieldProp({
                              key: "description",
                              value: e.target.value,
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Field Section",
              props: {
                header: {
                  title: "Field Section",
                  description:
                    "Organize this field under a collapsible section in CRM lists (visualization only)",
                },
                actions: field?.section
                  ? [
                      {
                        label: "Edit Field Section",
                        variant: "outline" as const,
                        size: "sm" as const,
                        handleClick: () => {
                          const current = field.section;
                          if (!current?.id && !current?.handle) return;
                          const nextName = window.prompt(
                            "Section name",
                            current.name,
                          );
                          if (nextName == null) return;
                          const trimmed = nextName.trim();
                          if (!trimmed) {
                            toast.error("Section name is required");
                            return;
                          }
                          void (async () => {
                            try {
                              let sectionId: string | undefined = current.id;
                              if (!sectionId) {
                                const listed =
                                  await trpc.crm.listFieldSections.query();
                                sectionId = listed.find(
                                  (s) => s.handle === current.handle,
                                )?.id;
                              }
                              if (!sectionId) {
                                toast.error(
                                  "Save the field first to create this section, then edit its name",
                                );
                                return;
                              }
                              const updated =
                                await trpc.crm.updateFieldSection.mutate({
                                  id: sectionId,
                                  name: trimmed,
                                });
                              store.dispatch(
                                renameFieldSectionInStore({
                                  id: updated.id,
                                  handle: updated.handle,
                                  name: updated.name,
                                }),
                              );
                              toast.success("Field section renamed");
                            } catch (error) {
                              console.error(error);
                              toast.error("Failed to rename field section");
                            }
                          })();
                        },
                      },
                      {
                        label: "Remove",
                        variant: "outline" as const,
                        size: "sm" as const,
                        handleClick: () => {
                          store.dispatch(
                            updateFieldSectionLocal({ section: null }),
                          );
                        },
                      },
                    ]
                  : [],
                form: {
                  formVersion: "v1",
                  id: "field-section-form",
                  fields: [
                    {
                      component: "Select" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "fieldSectionSelect",
                        label: "Section",
                        value: field?.section?.handle || "__none__",
                        placeholder: "No section",
                        helpText:
                          "Pick an existing section or create one with a handle below",
                        items: [
                          {
                            label: "No section",
                            value: "__none__",
                            description: "Show with unsectioned fields",
                          },
                          ...workplaceSectionOptions.map((section) => ({
                            label: section.name,
                            value: section.handle,
                            description: section.handle,
                          })),
                        ],
                        handleChange: (value: string) => {
                          if (value === "__none__") {
                            store.dispatch(
                              updateFieldSectionLocal({ section: null }),
                            );
                            return;
                          }
                          const existing = workplaceSectionOptions.find(
                            (s) => s.handle === value,
                          );
                          store.dispatch(
                            updateFieldSectionLocal({
                              section: existing
                                ? {
                                    handle: existing.handle,
                                    name: existing.name,
                                  }
                                : {
                                    handle: value,
                                    name: value
                                      .split(/[_-]+/)
                                      .filter(Boolean)
                                      .map(
                                        (part) =>
                                          part.charAt(0).toUpperCase() +
                                          part.slice(1).toLowerCase(),
                                      )
                                      .join(" "),
                                  },
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Input" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        type: "text" as const,
                        id: "fieldSectionCreateHandle",
                        label: "Create or assign by handle",
                        leftIcon: "Tag" as iconTypes,
                        value: field?.section?.handle || "",
                        placeholder: "e.g. profile",
                        helpText:
                          "Enter a handle to create or reuse a workplace section on save",
                        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                          const handle = e.target.value
                            .trim()
                            .toLowerCase()
                            .replace(/[^a-z0-9_-]/g, "");
                          if (!handle) {
                            store.dispatch(
                              updateFieldSectionLocal({ section: null }),
                            );
                            return;
                          }
                          const existing = workplaceSectionOptions.find(
                            (s) => s.handle === handle,
                          );
                          store.dispatch(
                            updateFieldSectionLocal({
                              section: existing
                                ? {
                                    handle: existing.handle,
                                    name: existing.name,
                                  }
                                : {
                                    handle,
                                    name: handle
                                      .split(/[_-]+/)
                                      .filter(Boolean)
                                      .map(
                                        (part) =>
                                          part.charAt(0).toUpperCase() +
                                          part.slice(1).toLowerCase(),
                                      )
                                      .join(" "),
                                  },
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Appearance",
              hidden: false,
              props: {
                header: {
                  title: "Appearance",
                  description:
                    "Configure how this field looks in forms and spreadsheets",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "IconSelector" as const,
                      span: 6 as SpanCol,
                      mdSpan: 6 as SpanCol,
                      lgSpan: 6 as SpanCol,
                      props: {
                        id: "fieldLeftIcon",
                        label: "Left Icon",
                        value:
                          ((field?.appearance as Record<string, unknown>)
                            ?.leftIcon as iconTypes | undefined) ?? null,
                        placeholder: "Select icon",
                        helpText: "Icon shown on the left side of the input",
                        onValueChange: (icon: iconTypes | null) => {
                          store.dispatch(
                            updateFieldAppearance({
                              key: "leftIcon",
                              value: icon,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "IconSelector" as const,
                      span: 6 as SpanCol,
                      mdSpan: 6 as SpanCol,
                      lgSpan: 6 as SpanCol,
                      props: {
                        id: "fieldRightIcon",
                        label: "Right Icon",
                        value:
                          ((field?.appearance as Record<string, unknown>)
                            ?.rightIcon as iconTypes | undefined) ?? null,
                        placeholder: "Select icon",
                        helpText: "Icon shown on the right side of the input",
                        onValueChange: (icon: iconTypes | null) => {
                          store.dispatch(
                            updateFieldAppearance({
                              key: "rightIcon",
                              value: icon,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Input" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        type: "text" as const,
                        id: "fieldPlaceholder",
                        label: "Placeholder",
                        value:
                          ((field?.appearance as Record<string, unknown>)
                            ?.placeholder as string | undefined) ?? "",
                        placeholder: "Enter placeholder text",
                        helpText:
                          "Placeholder text shown when the field is empty",
                        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                          store.dispatch(
                            updateFieldAppearance({
                              key: "placeholder",
                              value: e.target.value || null,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Textarea" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "fieldInlineHelpText",
                        label: "Help Text",
                        value:
                          ((field?.appearance as Record<string, unknown>)
                            ?.helpText as string | undefined) ?? "",
                        placeholder: "Enter inline help text",
                        helpText: "Help text shown below the input in forms",
                        rows: 2,
                        handleChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
                          store.dispatch(
                            updateFieldAppearance({
                              key: "helpText",
                              value: e.target.value || null,
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Default Value",
              hidden: false,
              props: {
                header: {
                  title: "Default Value",
                  description: "Configure the default value for this field",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: (() => {
                    const fieldType = field?.type;
                    const hasOptions =
                      field?.definition?.options &&
                      field?.definition?.options.length > 0;

                    // If there are options (but not allowMultiple for now, since MultiSelect isn't available)
                    if (hasOptions) {
                      return [
                        {
                          component: "MultiSelect" as const,
                          span: 12 as SpanCol,
                          mdSpan: 12 as SpanCol,
                          lgSpan: 12 as SpanCol,
                          props: {
                            label: "Default Value",
                            helpText:
                              "This is the default value for this field. Changing this field will required you to select a default value update strategy.",
                            placeholder: field?.isUnique
                              ? "Not available if this field is unique"
                              : "No options selected",
                            isDisabled: !!field?.isUnique,
                            limitChoices:
                              typeof field?.definition?.limitChoices ===
                                "number" &&
                              !Number.isNaN(field?.definition?.limitChoices)
                                ? field?.definition?.limitChoices
                                : undefined,
                            value:
                              (field?.defaultValue?.value as string[]) || [],
                            options: [
                              ...(field?.definition?.options?.map((option) => ({
                                label: option.label,
                                value: option.value,
                                color: option.color as string,
                              })) || []),
                            ],
                            handleValueChange: (values: string[]) => {
                              const newDefaultValue = {
                                value: values,
                                optionIds: (
                                  field?.definition?.options
                                    ?.filter((option) =>
                                      values.includes(option.value),
                                    )
                                    .map((option) => option.id) || []
                                ).filter(
                                  (id): id is string => typeof id === "string",
                                ),
                              };

                              if (field?.id) {
                                void store.dispatch(
                                  updateDefaultValueWithValidation({
                                    fieldId: field.id,
                                    defaultValue: newDefaultValue,
                                  }),
                                );
                              } else {
                                store.dispatch(
                                  updateFieldProp({
                                    key: "defaultValue",
                                    value: newDefaultValue,
                                  }),
                                );
                              }
                            },
                          },
                        },
                      ];
                    }
                    // Handle different data types
                    switch (fieldType) {
                      case DataType.NUMBER:
                        return [
                          {
                            component: "Input" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              type: "number" as const,
                              id: "fieldDefaultValue",
                              label: "Default Value",
                              leftIcon: "Hash" as iconTypes,
                              isDisabled: !!field?.isUnique,
                              isLoading: isValidating,
                              errorMessage: parseErrorCodeToMessage(
                                fieldError?.defaultValue,
                                field?.validationMessages,
                              ),
                              value: (() => {
                                const defaultValue = field?.defaultValue;
                                const valueArray = defaultValue?.value;
                                if (
                                  Array.isArray(valueArray) &&
                                  valueArray.length > 0
                                ) {
                                  return String(valueArray[0]);
                                }
                                return "";
                              })(),
                              placeholder: field?.isUnique
                                ? "Not available if this field is unique"
                                : "Enter default number",
                              helpText: `Default number to use when no value is provided`,
                              handleChange: (
                                e: ChangeEvent<HTMLInputElement>,
                              ) => {
                                const inputValue = e.target.value.trim();

                                let newDefaultValue;
                                if (inputValue === "") {
                                  // Empty input - set empty array
                                  newDefaultValue = {
                                    value: [],
                                    optionIds: [],
                                  };
                                } else {
                                  // Convert to number and validate
                                  const numValue = Number(inputValue);
                                  if (!isNaN(numValue)) {
                                    newDefaultValue = {
                                      value: [numValue],
                                      optionIds: [],
                                    };
                                  } else {
                                    // Invalid number - set empty array
                                    newDefaultValue = {
                                      value: [],
                                      optionIds: [],
                                    };
                                  }
                                }

                                if (field?.id) {
                                  void store.dispatch(
                                    updateDefaultValueWithValidation({
                                      fieldId: field.id,
                                      defaultValue: newDefaultValue,
                                    }),
                                  );
                                } else {
                                  // Update defaultValue directly in state for real-time updates
                                  store.dispatch(
                                    updateFieldProp({
                                      key: "defaultValue",
                                      value: newDefaultValue,
                                    }),
                                  );
                                }
                              },
                            },
                          },
                        ];

                      case DataType.BOOLEAN:
                        return [
                          {
                            component: "Select" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              id: "fieldDefaultValue",
                              label: "Default Value",
                              isDisabled: !!field?.isUnique,
                              value:
                                JSON.stringify(field?.defaultValue?.value) ||
                                "[]",
                              placeholder: field?.isUnique
                                ? "Not available if this field is unique"
                                : "Select default boolean value",
                              helpText:
                                "Default boolean value to use when no value is provided",
                              items: [
                                { label: "True", value: "[true]" },
                                { label: "False", value: "[false]" },
                              ],
                              handleChange: (value: string) => {
                                const newDefaultValue = {
                                  value: value
                                    ? (JSON.parse(value) as boolean[])
                                    : [],
                                  optionIds: [],
                                };

                                if (field?.id) {
                                  void store.dispatch(
                                    updateDefaultValueWithValidation({
                                      fieldId: field.id,
                                      defaultValue: newDefaultValue,
                                    }),
                                  );
                                } else {
                                  // Update defaultValue directly in state for real-time updates
                                  store.dispatch(
                                    updateFieldProp({
                                      key: "defaultValue",
                                      value: newDefaultValue,
                                    }),
                                  );
                                }
                              },
                            },
                          },
                        ];

                      case DataType.DATE:
                      case DataType.DATE_TIME:
                      case DataType.TIME:
                        return [
                          {
                            component: "DatePicker" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              id: "fieldDefaultValue",
                              label: "Default Value",
                              isDisabled: !!field?.isUnique,
                              value: field?.defaultValue?.value[0]
                                ? new Date(
                                    field?.defaultValue?.value[0] as string,
                                  )
                                : undefined,
                              placeholder: "Select default date value",
                              handleChange: (value: Date | undefined) => {
                                const newDefaultValue = {
                                  value: value ? [value.toISOString()] : [],
                                  optionIds: [],
                                };

                                if (field?.id) {
                                  void store.dispatch(
                                    updateDefaultValueWithValidation({
                                      fieldId: field.id,
                                      defaultValue: newDefaultValue,
                                    }),
                                  );
                                } else {
                                  // Update defaultValue directly in state for real-time updates
                                  store.dispatch(
                                    updateFieldProp({
                                      key: "defaultValue",
                                      value: newDefaultValue,
                                    }),
                                  );
                                }
                              },
                            },
                          },
                        ];

                      case DataType.STRING:
                      default:
                        return [
                          {
                            component: "Input" as const,
                            span: 12 as SpanCol,
                            mdSpan: 12 as SpanCol,
                            lgSpan: 12 as SpanCol,
                            props: {
                              type: "text" as const,
                              id: "fieldDefaultValue",
                              label: "Default Value",
                              leftIcon: "FileText" as iconTypes,
                              errorMessage: parseErrorCodeToMessage(
                                fieldError?.defaultValue,
                                field?.validationMessages,
                              ),
                              value: (() => {
                                const defaultValue = field?.defaultValue;
                                const valueArray = defaultValue?.value;
                                if (
                                  Array.isArray(valueArray) &&
                                  valueArray.length > 0
                                ) {
                                  return String(valueArray[0]);
                                }
                                return "";
                              })(),
                              placeholder: field?.isUnique
                                ? "Not available if this field is unique"
                                : "Enter default value",
                              isDisabled: !!field?.isUnique,
                              isLoading: isValidating,
                              helpText: `Default value to use when no value is provided`,
                              handleChange: (
                                e: ChangeEvent<HTMLInputElement>,
                              ) => {
                                const newDefaultValue = {
                                  strategy: isCreateMode
                                    ? "replace-all"
                                    : "replace-empty",
                                  value: [e.target.value],
                                  optionIds: [],
                                };
                                if (field?.id) {
                                  void store.dispatch(
                                    updateDefaultValueWithValidation({
                                      fieldId: field.id,
                                      defaultValue: newDefaultValue,
                                    }),
                                  );
                                } else {
                                  // Update defaultValue directly in state for real-time updates
                                  store.dispatch(
                                    updateFieldProp({
                                      key: "defaultValue",
                                      value: newDefaultValue,
                                    }),
                                  );
                                }
                              },
                            },
                          },
                        ];
                    }
                  })(),
                },
              },
            },
            {
              type: "Card",
              title: "Field Validation",
              props: {
                header: {
                  title: "Field Validation",
                  description: "Configure validation rules for this field",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Select" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "fieldRequirement",
                        label: "Field Requirement",
                        value: field?.requirement || FieldRequirement.ON_CREATE,
                        isDisabled: hasCommunicationChannel,
                        placeholder: "Select field requirement",
                        helpText: "Define when this field is required",
                        items: [
                          {
                            icon: "TriangleAlert",
                            label: "Required",
                            value: FieldRequirement.REQUIRED,
                            description:
                              "Field is always required, on updates and creation",
                          },
                          {
                            icon: "PackagePlus",
                            label: "Required on Create",
                            value: FieldRequirement.ON_CREATE,
                            description:
                              "Field is required only when creating new resources, and can be optionally updated after the resource is created",
                          },
                          {
                            icon: "RectangleEllipsis",
                            label: "Optional",
                            value: FieldRequirement.OPTIONAL,
                            description:
                              "Field is never required, and can be optionally updated",
                          },
                        ],
                        handleChange: (value: string) => {
                          store.dispatch(
                            updateFieldProp({
                              key: "requirement",
                              value: value as FieldRequirement,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Input" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden:
                        field?.requirement !== FieldRequirement.REQUIRED &&
                        field?.requirement !== FieldRequirement.ON_CREATE,
                      props: {
                        type: "text" as const,
                        id: "validationRequiredMessage",
                        leftIcon: "MessageCircleX" as iconTypes,
                        value:
                          field?.validationMessages?.REQUIRED ||
                          "This field is required",
                        placeholder: "This field is required",
                        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                          store.dispatch(
                            updateFieldProp({
                              key: "validationMessages",
                              value: {
                                ...toValidationMessageRecord(
                                  field?.validationMessages,
                                ),
                                REQUIRED: e.target.value,
                              },
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Checkbox" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: isUniqueHidden(),
                      props: {
                        id: "fieldUnique",
                        label: "Unique Field",
                        checked: field?.isUnique || false,
                        isDisabled: hasCommunicationChannel,
                        helpText: "Ensure all values in this field are unique",
                        handleChange: (checked: boolean) => {
                          store.dispatch(
                            updateFieldProp({
                              key: "isUnique",
                              value: checked,
                            }),
                          );

                          // Clear default values when field is set to unique
                          if (checked) {
                            // Update defaultValue directly in state for real-time updates
                            store.dispatch(
                              updateFieldProp({
                                key: "defaultValue",
                                value: { value: [], optionIds: [] },
                              }),
                            );
                          }
                        },
                      },
                    },
                    {
                      component: "Input" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: !field?.isUnique,
                      props: {
                        type: "text" as const,
                        id: "validationUniqueMessage",
                        leftIcon: "MessageCircleX" as iconTypes,
                        value:
                          field?.validationMessages?.UNIQUE ||
                          `A value for this field must be unique`,
                        placeholder: `The value for this field must be unique`,
                        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                          store.dispatch(
                            updateFieldProp({
                              key: "validationMessages",
                              value: {
                                ...toValidationMessageRecord(
                                  field?.validationMessages,
                                ),
                                UNIQUE: e.target.value,
                              },
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "AI Settings",
              hidden: field?.system,
              props: {
                header: {
                  title: "AI Settings",
                  description:
                    "Configure how AI agents interact with this field",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Select" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        id: "aiMutability",
                        label: "AI Access Level",
                        value: field?.aiMeta?.mutability || "always",
                        placeholder: "Select AI access level",
                        helpText:
                          "Controls how AI agents can modify this field",
                        items: [
                          {
                            icon: "Unlock" as iconTypes,
                            label: "Full Access",
                            value: "always",
                            description:
                              "AI can read and update this field anytime",
                          },
                          {
                            icon: "ShieldAlert" as iconTypes,
                            label: "Limited Access",
                            value: "restricted",
                            description:
                              "AI can set on create, updates require explicit user request",
                          },
                          {
                            icon: "PackagePlus" as iconTypes,
                            label: "Set Once",
                            value: "create_only",
                            description:
                              "AI can only set this field when creating a record",
                          },
                          {
                            icon: "Lock" as iconTypes,
                            label: "Disabled",
                            value: "immutable",
                            description: "AI cannot modify this field",
                          },
                        ],
                        handleChange: (value: string) => {
                          store.dispatch(
                            updateFieldAiMeta({
                              key: "mutability",
                              value: value as
                                | "always"
                                | "restricted"
                                | "create_only"
                                | "immutable",
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Textarea" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: field?.aiMeta?.mutability === "immutable",
                      props: {
                        id: "aiDescription",
                        label: "AI Description",
                        value: field?.aiMeta?.description || "",
                        placeholder: "Leave empty to auto-generate on save",
                        helpText:
                          "Helps AI understand how to populate this field. Auto-generated if left empty.",
                        rows: 3,
                        handleChange: (e: ChangeEvent<HTMLTextAreaElement>) => {
                          store.dispatch(
                            updateFieldAiMeta({
                              key: "description",
                              value: e.target.value || undefined,
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Definition",
              props: {
                header: {
                  title: "Definition",
                  description: "Configure the field's data type and format",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Checkbox" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: isEditMode,
                      props: {
                        label: "Use Custom Definition",
                        checked: field?.definition?.isPrivate,
                        disabled: hasCommunicationChannel,

                        helpText:
                          "Custom Definitions allow you to add whatever options and normalizations you want, built-ins are useful for common use cases and datatypes such as phone numbers / emails / etc",
                        handleChange: (checked: boolean) => {
                          if (checked) {
                            store.dispatch(
                              resetFieldDefinition({
                                handle: `${workplace?.subdomain || ""}/${model?.handle || ""}/${field?.handle || ""}`,
                              }),
                            );

                            store.dispatch(
                              updateFieldDefinitionProp({
                                key: "isPrivate",
                                value: true,
                              }),
                            );
                          } else {
                            store.dispatch(
                              updateFieldDefinitionProp({
                                key: "isPrivate",
                                value: false,
                              }),
                            );
                          }
                        },
                      },
                    },
                    {
                      component: "Alert" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: field?.definition?.isPrivate,
                      props: {
                        icon: "Info",
                        title: `${field?.definition?.handle || "Select a definition"}`,
                        description:
                          (field?.definition?.description as string) ||
                          "No description available",
                        action: {
                          isDisabled: isEditMode || hasCommunicationChannel,
                          label: field?.definition?.handle
                            ? "Change Definition"
                            : "Select a definition",
                          variant: "outline",
                          size: "sm",
                          handleClick: async () => {
                            const definitions =
                              await trpc.crm.getDefinitions.query({
                                public: true,
                                ...(field?.id ? { type: field?.type } : {}),
                              });

                            store.dispatch(
                              openDialog({
                                schema: "ADD_BUILT_IN_DEFINITION",
                                params: {
                                  definitions,
                                  definitionId: definitions[0]?.id,
                                },
                              }),
                            );
                          },
                        },
                      },
                    },
                    {
                      component: "Select" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: !field?.definition?.isPrivate,
                      props: {
                        id: "fieldType",
                        label: "Data Type",
                        value: field?.type,
                        isDisabled: !isCreateMode || hasCommunicationChannel,
                        placeholder: "Select data type",
                        helpText: "The data type for this field",
                        items: dataTypeOptions,

                        handleChange: (value: string) => {
                          store.dispatch(
                            updateFieldProp({
                              key: "type",
                              value: value,
                            }),
                          );

                          // Set default value depending on type
                          if (value === DataType.BOOLEAN) {
                            // Update defaultValue directly in state for real-time updates
                            store.dispatch(
                              updateFieldProp({
                                key: "defaultValue",
                                value: { value: [false], optionIds: [] },
                              }),
                            );
                          } else {
                            // Update defaultValue directly in state for real-time updates
                            store.dispatch(
                              updateFieldProp({
                                key: "defaultValue",
                                value: { value: [], optionIds: [] },
                              }),
                            );
                          }

                          /** if field is boolean, we need to clear the options */
                          store.dispatch(
                            updateFieldDefinitionProp({
                              key: "options",
                              value: [],
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Checkbox" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: isRegexHidden(),
                      props: {
                        id: "regex",
                        label: "Regex",
                        checked: fieldOptions.regexEnabled,
                        disabled:
                          (!field && !isCreateMode) || hasCommunicationChannel,
                        helpText: "Validate the value using a regex pattern",
                        handleChange: (checked: boolean) => {
                          if (checked) {
                            store.dispatch(enableRegex());
                          } else {
                            store.dispatch(disableRegex());
                          }
                        },
                      },
                    },
                    {
                      component: "Input" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: isRegexPatternHidden(),
                      props: {
                        type: "text" as const,
                        id: "regexPattern",
                        label: "Regex Pattern",
                        leftIcon: "Regex" as iconTypes,
                        value: field?.definition?.regexPattern || ``,
                        placeholder: `Enter regex pattern without slashes eg. ^[a-zA-Z0-9]+$`,
                        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                          store.dispatch(
                            updateFieldDefinitionProp({
                              key: "regexPattern",
                              value: e.target.value,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "Input" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: isRegexPatternHidden(),
                      props: {
                        type: "text" as const,
                        id: "validationRegexMessage",
                        label: "Regex Error Message",
                        leftIcon: "MessageCircleX" as iconTypes,
                        value:
                          (field?.validationMessages || {})?.REGEX ||
                          `The value does not match the required format`,
                        placeholder: `The value does not match the required format`,
                        handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                          store.dispatch(
                            updateFieldProp({
                              key: "validationMessages",
                              value: {
                                ...toValidationMessageRecord(
                                  field?.validationMessages,
                                ),
                                REGEX: e.target.value,
                              },
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Normalizations",
              hidden: isNormalizationsHidden(),
              props: {
                header: {
                  title: "Normalizations",
                  description: "Configure the field's normalizations",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "MultiSelect",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        label: `Normalizations`,
                        helpText:
                          "Normalizations parse data before it is saved to the database, keeping data clean and consistent, the original data will be used if the normalization fails",
                        placeholder: "Select Normalizations",
                        options: normalizations
                          .filter(
                            (normalization) =>
                              normalization.type === field?.type,
                          )
                          .map((normalization) => ({
                            label: normalization.name,
                            value: normalization.handle,
                            description: normalization.description,
                            icon: normalization.icon as iconTypes,
                          })),
                        hideColors: true,
                        handleValueChange: (values: string[]) => {
                          store.dispatch(
                            updateFieldDefinitionProp({
                              key: "normalizations",
                              value: values.map((value) => ({
                                handle: value,
                                sortOrder: 0,
                                arguments: {},
                              })),
                            }),
                          );
                        },
                        value:
                          field?.definition?.normalizations?.map(
                            (normalization) => normalization.handle,
                          ) || [],
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Options",
              hidden: isOptionsHidden(),
              props: {
                header: {
                  title: "Options",
                  description: "Configure the field's options",
                },
                secondaryActions: [
                  {
                    label: "Add Option",
                    variant: "outline",
                    isDisabled: hasCommunicationChannel,
                    handleClick: () => {
                      const { color, excludedColors } = getColorForOption(
                        field?.definition?.options || [],
                      );

                      store.dispatch(
                        openDialog({
                          schema: "EDIT_DEFINITION_OPTION",
                          params: {
                            fieldId: field?.id || "",
                            definitionId: field?.definition?.id,
                            id: "",
                            label: "",
                            value: "",
                            color,
                            object: "{}",
                            isEditing: false,
                            default: false,
                            excludedColors,
                            originalDefaultValue:
                              fieldOptions.originalDefaultValue,
                            dataType: field?.type || "STRING",
                            regexPattern:
                              field?.definition?.regexPattern || undefined,
                          },
                        }),
                      );
                    },
                  },
                ],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "Checkbox",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      hidden: !field?.definition?.options?.length,
                      props: {
                        id: "allowMultipleOptions",
                        label: "Allow multiple options to be selected",
                        checked:
                          field?.definition?.limitChoices === undefined ||
                          field?.definition?.limitChoices === null ||
                          field?.definition?.limitChoices > 1,
                        disabled: hasCommunicationChannel,
                        helpText:
                          "This is useful for fields like tags, categories, or multiple choice questions",
                        handleChange: (checked: boolean) => {
                          store.dispatch(
                            updateFieldDefinitionProp({
                              key: "limitChoices",
                              value: checked ? null : 1,
                            }),
                          );
                        },
                      },
                    },
                    {
                      component: "DataTable",
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        data: field?.definition?.options || [],
                        emptyActions: [
                          {
                            label: "Add Option",
                            variant: "outline",
                            isDisabled:
                              !field?.definition?.isPrivate ||
                              hasCommunicationChannel,
                            size: "sm",
                            handleClick: () => {
                              const { color, excludedColors } =
                                getColorForOption(
                                  field?.definition?.options || [],
                                );
                              store.dispatch(
                                openDialog({
                                  schema: "EDIT_DEFINITION_OPTION",
                                  params: {
                                    fieldId: field?.id || "",
                                    id: "",
                                    label: "",
                                    value: "",
                                    color,
                                    object: "{}",
                                    originalDefaultValue:
                                      fieldOptions.originalDefaultValue,
                                    isEditing: false,
                                    definitionId: field?.definition?.id,
                                    default: false,
                                    excludedColors,
                                    dataType: field?.type || "STRING",
                                    regexPattern:
                                      field?.definition?.regexPattern ||
                                      undefined,
                                  },
                                }),
                              );
                            },
                          },
                        ],
                        emptyMessage:
                          "No options configured. Add options for users to select from.",
                        columns: [
                          {
                            id: "label",
                            type: "rowLayout",
                            header: "Label",
                            accessorKey: "label",
                            align: "left",
                            components: [
                              {
                                component: "Color",
                                generateProps: (row: {
                                  id?: string;
                                  label: string;
                                  value: string;
                                  color?: string | null;
                                }) => {
                                  return {
                                    color: row.color || "#3b82f6",
                                  };
                                },
                              },
                              {
                                component: "Text",
                                generateProps: (row: {
                                  id?: string;
                                  label: string;
                                  value: string;
                                  color?: string | null;
                                }) => {
                                  return {
                                    children: row.label,
                                  };
                                },
                              },
                            ],
                          },
                          {
                            id: "value",
                            type: "rowLayout",
                            header: "Value",
                            accessorKey: "value",
                            align: "left",
                            components: [
                              {
                                component: "Text",
                                generateProps: (row: {
                                  id?: string;
                                  label: string;
                                  value: string;
                                  color?: string | null;
                                }) => {
                                  return {
                                    children: row.value,
                                  };
                                },
                              },
                            ],
                          },
                        ],
                        onRowClick: (option: {
                          id?: string;
                          label: string;
                          value: string;
                          color?: string | null;
                        }) => {
                          if (hasCommunicationChannel) {
                            return;
                          }
                          const { color, excludedColors } = getColorForOption(
                            field?.definition?.options || [],
                            option.color,
                          );

                          store.dispatch(
                            openDialog({
                              schema: "EDIT_DEFINITION_OPTION",
                              params: {
                                fieldId: field?.id || "",
                                definitionId: field?.definition?.id,
                                id: "id" in option ? option.id : undefined,
                                label: option.label || "",
                                value: option.value || "",
                                color,
                                originalDefaultValue:
                                  fieldOptions.originalDefaultValue,
                                isEditing: true,
                                excludedColors,
                                dataType: field?.type || "STRING",
                                regexPattern:
                                  field?.definition?.regexPattern || undefined,
                              },
                            }),
                          );
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Linked Apps",
              hidden:
                !isEditMode ||
                !field?.id ||
                !(
                  field as unknown as {
                    linkedApps?: Array<unknown>;
                  }
                )?.linkedApps?.length,
              props: {
                header: {
                  title: "Linked Apps",
                  description:
                    "Apps that have mapped this field to their resources",
                },
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "DataTable" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        data:
                          (
                            field as unknown as {
                              linkedApps?: Array<{
                                id: string;
                                appName: string;
                                appHandle: string;
                                installId: string;
                                resourceHandle: string;
                                scope: "INTERNAL" | "SHARED";
                                iconUrl: string | null;
                                accentColor: string | null;
                              }>;
                            }
                          )?.linkedApps || [],
                        emptyMessage:
                          "No apps have linked this field to their resources.",
                        columns: [
                          {
                            id: "appName",
                            type: "rowLayout" as const,
                            header: "App",
                            accessorKey: "appName",
                            align: "left" as const,
                            components: [
                              {
                                component: "Text" as const,
                                generateProps: (row: { appName: string }) => ({
                                  children: row.appName || "",
                                }),
                              },
                            ],
                          },
                          {
                            id: "resourceHandle",
                            type: "rowLayout" as const,
                            header: "Resource",
                            accessorKey: "resourceHandle",
                            align: "left" as const,
                            components: [
                              {
                                component: "Text" as const,
                                generateProps: (row: {
                                  resourceHandle: string;
                                }) => ({
                                  children: row.resourceHandle || "",
                                }),
                              },
                            ],
                          },
                        ],
                        onRowClick: (row: {
                          appHandle: string;
                          installId: string;
                          scope: "INTERNAL" | "SHARED";
                        }) => {
                          const url =
                            row.scope === "INTERNAL"
                              ? `/${subdomain}/developers/apps/${row.appHandle}/general`
                              : `/${subdomain}/settings/apps/${row.appHandle}/install/${row.installId}`;

                          store.dispatch(
                            closeSideNavigation({ position: "right" }),
                          );
                          setTimeout(() => {
                            router.push(url);
                          }, 0);
                        },
                      },
                    },
                  ],
                },
              },
            },
            {
              type: "Card",
              title: "Danger Zone",
              hidden: !isEditMode || !field?.id,
              props: {
                header: {
                  title: "Danger Zone",
                  description:
                    "Once you delete a field, there is no going back. Please be certain.",
                },
                actions: [],
                form: {
                  formVersion: "v1",
                  fields: [
                    {
                      component: "FieldSetting" as const,
                      span: 12 as SpanCol,
                      mdSpan: 12 as SpanCol,
                      lgSpan: 12 as SpanCol,
                      props: {
                        label: "Delete Field",
                        description: `Permanently delete the "${field?.label || "Untitled Field"}" field and all associated data.`,
                        mode: "field" as const,
                        button: {
                          variant: "destructive" as const,
                          size: "sm" as const,
                          label: "Delete Field",
                          leftIcon: "Trash2" as const,
                          handleClick: () => {
                            if (!field?.id) {
                              return;
                            }
                            void openDeleteFieldDialog(
                              field.id,
                              field.label || "Untitled Field",
                            );
                          },
                        },
                      },
                    },
                  ],
                },
              },
            },
          ],
        },
      },
    },
  };
};
